import {
  createPlugins,
  createBoldPlugin,
  createItalicPlugin,
  createListPlugin,
  createTablePlugin,
  createUnderlinePlugin,
  createSoftBreakPlugin,
} from '@udecode/plate';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import clearEditorAndHistory from 'components/text_editor_new/lib/clear_editor_and_history';
import components from 'components/text_editor_new/plugins/components';
import createCapitalizeFirstWordPlugin from 'components/text_editor_new/plugins/capitalize_first_word';
import createCapitalizeIPlugin from 'components/text_editor_new/plugins/capitalize_i';
import { createEditorEventsPlugin } from 'components/text_editor_new/plugins/editor_events/create_editor_events_plugin';
import { createFontColorPlugin } from 'components/text_editor_new/plugins/font_style/font_color';
import { createFontElementPlugin } from 'components/text_editor_new/plugins/font_style/font_element';
import { createFontSizePlugin } from 'components/text_editor_new/plugins/font_style/font_size';
import { createHotkeyPlugin } from 'components/text_editor_new/plugins/hotkey/create_hotkey_plugin';
import createInsertTextAfterInline from 'components/text_editor_new/plugins/insert_text_after_inline';
import { createLinkPlugin } from 'components/text_editor_new/plugins/inline_links/create_link_plugin';
import { createLinkSelectionPlugin } from 'components/text_editor_new/plugins/inline_links/create_link_selection_plugin';
import createParagraphPlugin from 'components/text_editor_new/plugins/paragraph';
import { createTableNormalizationPlugin } from 'components/text_editor_new/plugins/table/create_table_normalization_plugin';
import createTextAlignmentPlugin from 'components/text_editor_new/plugins/text_alignment';
import { editorHasNoText } from 'components/text_editor_new/lib/editor_has_no_text';
import normalizeEditor from 'components/text_editor_new/lib/normalize_editor';
import SendTaskThreadEmail from 'actions/task/send_task_thread_email';
import serializeEmailBody from 'components/text_editor_new/lib/serialize_email_body';
import useEditorWithDecorators from 'components/text_editor_new/hooks/use_editor_with_decorators';
import { useExecuteAction } from 'components/hooks/connect_hooks';

export default function useTaskEmailEditor(editorProps, sendContextRef, onSendErrors) {
  const { editorId } = editorProps;

  const plugins = useMemo(() => {
    // The same formatting the email composer offers (see useRichTextEditorPlugins), without the
    // composition-bound pieces: answers, variables, attachments, inline images, AI.
    return createPlugins(
      [
        createInsertTextAfterInline(),
        createHotkeyPlugin(),
        createEditorEventsPlugin(),
        createCapitalizeIPlugin(),
        createCapitalizeFirstWordPlugin(),
        createBoldPlugin(),
        createItalicPlugin(),
        createUnderlinePlugin(),
        createFontColorPlugin(),
        createFontSizePlugin(),
        createFontElementPlugin(),
        createListPlugin(),
        createTablePlugin(),
        createTableNormalizationPlugin(),
        createParagraphPlugin(),
        createTextAlignmentPlugin(),
        createLinkPlugin(),
        createLinkSelectionPlugin(),
        createSoftBreakPlugin(),
      ],
      { components }
    );
  }, []);

  const editor = useEditorWithDecorators(plugins, { editorId });
  const editorRef = useRef(editor);

  const [onSubmit, isSending] = useOnSubmit(editorRef, sendContextRef, onSendErrors);

  editorProps.editorRef = editorRef;

  return [editorRef, onSubmit, isSending];
}

// One send at a time: a second click while the first request is in flight would post a duplicate
// message into the thread. The callbacks land after a round trip, and the editor is remounted per task
// and unmounted with the panel, so they check the component is still here before touching it.
function useOnSubmit(editorRef, sendContextRef, onSendErrors) {
  const executeAction = useExecuteAction();
  const [isSending, setIsSending] = useState(false);
  const sendingRef = useRef(false);
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const onSubmit = useCallback(
    evt => {
      const editor = editorRef.current;
      if (sendingRef.current || !editor || editorHasNoText(editor)) {
        return;
      }

      evt?.preventDefault?.();
      normalizeEditor(editor, { trimEmptyParagraphs: true, trimParagraphWhitespace: true });
      sendingRef.current = true;
      setIsSending(true);

      onSendErrors?.([]);
      const { taskId, from, to, cc, subject, dueAt } = sendContextRef.current;
      executeAction(SendTaskThreadEmail, {
        taskId,
        from,
        to,
        cc,
        subject,
        dueAt,
        bodyHtml: serializeEmailBody(editor),
        onSent: () => isMountedRef.current && clearEditorAndHistory(editor),
        onError: errors => isMountedRef.current && onSendErrors?.(errors),
        onSettled: () => {
          sendingRef.current = false;
          if (isMountedRef.current) setIsSending(false);
        },
      });
    },
    [editorRef, sendContextRef, executeAction, onSendErrors]
  );

  return [onSubmit, isSending];
}
